import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  FormGroup,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as PaidIcon,
  History as HistoryIcon,
  Cancel as CancelIcon,
  Undo as UndoIcon,
  Delete as DeleteIcon,
  VpnKey as PasswordResetIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  ManageAccounts as RolesIcon,
  AttachMoney as CommissionIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import {
  generatePasswordResetLink,
  sendPasswordResetEmail,
} from '../../services/auth-admin.service';
import { IClient, IClientLog, IPayment, UserRole, ROLE_LABELS, CLIENT_STATUS_LABELS, CLIENT_STATUS_COLORS, PaymentStatus, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS } from '../../types';

interface IUserCommission {
  id: string;
  name: string;
  email: string;
  affiliate_code: string;
  roles: UserRole[];
  commission_value: number;
}

interface IPlatformSettings {
  default_commission_value: number;
  client_discount: number;
  client_discount_type: 'fixed' | 'percentage';
  commission_rules_text: string;
}

interface IPaymentSummary {
  pending: { total_value: number; count: number };
  paid: { total_value: number; count: number };
  cancelled: { total_value: number; count: number };
  total: { total_value: number; count: number };
}

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<IUserCommission[]>([]);
  const [clients, setClients] = useState<IClient[]>([]);
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<IPaymentSummary | null>(null);
  const [platformSettings, setPlatformSettings] = useState<IPlatformSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentFilter, setPaymentFilter] = useState<PaymentStatus | ''>('');
  const [clientStatusFilter, setClientStatusFilter] = useState<string>('');

  // Estados para ações de pagamento
  const [cancelPaymentOpen, setCancelPaymentOpen] = useState(false);
  const [deletePaymentOpen, setDeletePaymentOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<IPayment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editCommissionOpen, setEditCommissionOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserCommission | null>(null);
  const [newRoles, setNewRoles] = useState<UserRole[]>([]);
  const [newCommission, setNewCommission] = useState(0);
  const [saving, setSaving] = useState(false);

  const [clientDetailsOpen, setClientDetailsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [clientLogs, setClientLogs] = useState<IClientLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [passwordResetLink, setPasswordResetLink] = useState('');
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [passwordResetSending, setPasswordResetSending] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/settings/commissions');
      const data = response.data.data;
      // Suporta tanto formato { users: [...] } quanto array direto
      const usersList = Array.isArray(data) ? data : (data?.users || []);
      setUsers(usersList);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar usuários');
      setUsers([]);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const params: any = { limit: 100 };
      if (clientStatusFilter) params.status = clientStatusFilter;
      const response = await api.get('/clients/list', { params });
      setClients(response.data.data.clients);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar clientes');
    }
  }, [clientStatusFilter]);

  const fetchPayments = useCallback(async () => {
    try {
      const params = paymentFilter ? { status: paymentFilter } : {};
      const [paymentsRes, summaryRes] = await Promise.all([
        api.get('/payments/list', { params }),
        api.get('/payments/summary'),
      ]);
      setPayments(paymentsRes.data.data.payments);
      setPaymentSummary(summaryRes.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar pagamentos');
    }
  }, [paymentFilter]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings/platform');
      setPlatformSettings(response.data.data);
    } catch {
      setPlatformSettings({ default_commission_value: 0, client_discount: 0, client_discount_type: 'fixed', commission_rules_text: '' });
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchClients(), fetchPayments(), fetchSettings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchClients, fetchPayments, fetchSettings]);

  useEffect(() => {
    fetchPayments();
  }, [paymentFilter, fetchPayments]);

  useEffect(() => {
    fetchClients();
  }, [clientStatusFilter, fetchClients]);

  const handleEditRoles = (user: IUserCommission) => {
    setSelectedUser(user);
    setNewRoles([...user.roles]);
    setEditUserOpen(true);
  };

  const handleEditCommission = (user: IUserCommission) => {
    setSelectedUser(user);
    setNewCommission(user.commission_value);
    setEditCommissionOpen(true);
  };

  const handleSaveRoles = async () => {
    if (!selectedUser || newRoles.length === 0) return;

    try {
      setSaving(true);
      await api.patch(`/users/update-roles/${selectedUser.id}`, { roles: newRoles });
      setSuccess('Roles atualizadas com sucesso!');
      setEditUserOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar roles');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCommission = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      await api.patch(`/settings/commissions/${selectedUser.id}`, {
        commission_value: newCommission,
      });
      setSuccess('Comissão atualizada com sucesso!');
      setEditCommissionOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar comissão');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!platformSettings) return;

    try {
      setSaving(true);
      await api.patch('/settings/platform', platformSettings);
      setSuccess('Configurações salvas com sucesso!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (paymentId: string) => {
    try {
      await api.patch(`/payments/mark-paid/${paymentId}`);
      setSuccess('Pagamento marcado como pago!');
      fetchPayments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao marcar pagamento');
    }
  };

  const handleOpenCancelPayment = (payment: IPayment) => {
    setSelectedPayment(payment);
    setCancelReason('');
    setCancelPaymentOpen(true);
  };

  const handleCancelPayment = async () => {
    if (!selectedPayment) return;
    try {
      setActionLoading(true);
      await api.patch(`/payments/cancel/${selectedPayment.id}`, { reason: cancelReason });
      setSuccess('Pagamento cancelado com sucesso!');
      setCancelPaymentOpen(false);
      fetchPayments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao cancelar pagamento');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevertToPending = async (paymentId: string) => {
    try {
      await api.patch(`/payments/revert/${paymentId}`);
      setSuccess('Pagamento revertido para pendente!');
      fetchPayments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao reverter pagamento');
    }
  };

  const handleOpenDeletePayment = (payment: IPayment) => {
    setSelectedPayment(payment);
    setDeleteReason('');
    setDeletePaymentOpen(true);
  };

  const handleDeletePayment = async () => {
    if (!selectedPayment || !deleteReason.trim()) {
      setError('Informe o motivo da exclusão');
      return;
    }
    try {
      setActionLoading(true);
      await api.delete(`/payments/${selectedPayment.id}`, { data: { reason: deleteReason } });
      setSuccess('Pagamento excluído com sucesso!');
      setDeletePaymentOpen(false);
      fetchPayments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir pagamento');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRoleToggle = (role: UserRole) => {
    setNewRoles((prev) => {
      if (prev.includes(role)) {
        return prev.filter((r) => r !== role);
      }
      return [...prev, role];
    });
  };

  const handleViewClientHistory = async (client: IClient) => {
    setSelectedClient(client);
    setClientDetailsOpen(true);
    setLoadingLogs(true);

    try {
      const response = await api.get(`/clients/get/${client.id}`);
      setClientLogs(response.data.data.logs);
    } catch {
      setClientLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleOpenPasswordReset = async (user: IUserCommission) => {
    setSelectedUser(user);
    setPasswordResetLink('');
    setPasswordResetOpen(true);
    setPasswordResetLoading(true);
    setError('');

    try {
      const data = await generatePasswordResetLink(user.id);
      setPasswordResetLink(data.password_reset_link);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Erro ao gerar link de recuperação de senha',
      );
      setPasswordResetOpen(false);
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleCopyPasswordResetLink = async () => {
    if (!passwordResetLink) return;

    try {
      await navigator.clipboard.writeText(passwordResetLink);
      setSuccess('Link copiado para a área de transferência!');
    } catch {
      setError('Não foi possível copiar o link');
    }
  };

  const handleSendPasswordResetEmail = async () => {
    if (!selectedUser) return;

    try {
      setPasswordResetSending(true);
      setError('');
      await sendPasswordResetEmail(selectedUser.id);
      setSuccess(`Email de recuperação enviado para ${selectedUser.email}`);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Erro ao enviar email de recuperação de senha',
      );
    } finally {
      setPasswordResetSending(false);
    }
  };

  const refreshAll = () => {
    fetchUsers();
    fetchClients();
    fetchPayments();
    fetchSettings();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Painel Administrativo
        </Typography>
        <IconButton onClick={refreshAll}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Usuários" />
        <Tab label="Clientes" />
        <Tab label="Configurações" />
        <Tab label="Pagamentos" />
      </Tabs>

      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Comissão</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(Array.isArray(users) ? users : []).map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.affiliate_code}</TableCell>
                  <TableCell>
                    {(user.roles || []).map((role) => (
                      <Chip
                        key={role}
                        label={ROLE_LABELS[role]}
                        size="small"
                        sx={{ mr: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>R$ {user.commission_value.toFixed(2)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Tooltip title="Editar roles do usuário">
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<RolesIcon fontSize="small" />}
                          onClick={() => handleEditRoles(user)}
                        >
                          Roles
                        </Button>
                      </Tooltip>
                      <Tooltip title="Editar comissão do usuário">
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<CommissionIcon fontSize="small" />}
                          onClick={() => handleEditCommission(user)}
                        >
                          Comissão
                        </Button>
                      </Tooltip>
                      <Tooltip title="Gerar link de recuperação de senha">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenPasswordReset(user)}
                          aria-label="Gerar link de recuperação de senha"
                        >
                          <PasswordResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabValue === 1 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Select
              size="small"
              value={clientStatusFilter}
              onChange={(e) => setClientStatusFilter(e.target.value)}
              displayEmpty
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Todos os status</MenuItem>
              <MenuItem value="pending_contact">Pendente de Contato</MenuItem>
              <MenuItem value="call_scheduled">Call Agendada</MenuItem>
              <MenuItem value="no_show">No-Show</MenuItem>
              <MenuItem value="not_interested">Sem Interesse</MenuItem>
              <MenuItem value="did_not_buy">Não comprou</MenuItem>
              <MenuItem value="bought">Comprou!</MenuItem>
            </Select>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Afiliado</TableCell>
                  <TableCell>Comercial</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        Nenhum cliente encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>{client.phone}</TableCell>
                      <TableCell>
                        <Chip
                          label={CLIENT_STATUS_LABELS[client.status]}
                          color={CLIENT_STATUS_COLORS[client.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{client.affiliate?.name || '-'}</TableCell>
                      <TableCell>{client.commercial?.name || '-'}</TableCell>
                      <TableCell>
                        {new Date(client.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Ver histórico">
                          <IconButton
                            size="small"
                            onClick={() => handleViewClientHistory(client)}
                            color="primary"
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabValue === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Comissão Padrão
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Valor da comissão aplicada para usuários que não possuem comissão personalizada.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  sx={{ minWidth: 250 }}
                  label="Valor da comissão padrão (R$)"
                  type="number"
                  value={platformSettings?.default_commission_value || 0}
                  onChange={(e) =>
                    setPlatformSettings((prev) =>
                      prev ? { ...prev, default_commission_value: Number(e.target.value) } : null
                    )
                  }
                  inputProps={{ min: 0 }}
                />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Desconto do Cliente
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Desconto aplicado automaticamente para clientes indicados.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  sx={{ minWidth: 200 }}
                  label="Valor do desconto"
                  type="number"
                  value={platformSettings?.client_discount || 0}
                  onChange={(e) =>
                    setPlatformSettings((prev) =>
                      prev ? { ...prev, client_discount: Number(e.target.value) } : null
                    )
                  }
                  inputProps={{ min: 0 }}
                />
                <Select
                  sx={{ minWidth: 180 }}
                  value={platformSettings?.client_discount_type || 'fixed'}
                  onChange={(e) =>
                    setPlatformSettings((prev) =>
                      prev
                        ? { ...prev, client_discount_type: e.target.value as 'fixed' | 'percentage' }
                        : null
                    )
                  }
                >
                  <MenuItem value="fixed">Fixo (R$)</MenuItem>
                  <MenuItem value="percentage">Percentual (%)</MenuItem>
                </Select>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Regras de Comissão
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Texto exibido para os indicadores na aba "Regras". Suporta HTML para formatação.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Texto das regras"
                placeholder="<p>Digite aqui as regras de comissão...</p>"
                value={platformSettings?.commission_rules_text || ''}
                onChange={(e) =>
                  setPlatformSettings((prev) =>
                    prev ? { ...prev, commission_rules_text: e.target.value } : null
                  )
                }
                helperText="Use tags HTML como <p>, <strong>, <ul>, <li> para formatação"
              />
            </CardContent>
          </Card>

          <Box>
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              disabled={saving}
              size="large"
            >
              {saving ? <CircularProgress size={20} /> : 'Salvar Configurações'}
            </Button>
          </Box>
        </Box>
      )}

      {tabValue === 3 && (
        <Box>
          {paymentSummary && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Card sx={{ flex: 1, minWidth: 180, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pendente
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    R$ {paymentSummary.pending.total_value.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paymentSummary.pending.count} pagamento(s)
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 180, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pago
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    R$ {paymentSummary.paid.total_value.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paymentSummary.paid.count} pagamento(s)
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 180, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cancelado
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    R$ {paymentSummary.cancelled?.total_value?.toFixed(2) || '0.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paymentSummary.cancelled?.count || 0} pagamento(s)
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 180 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    R$ {paymentSummary.total.total_value.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paymentSummary.total.count} pagamento(s)
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Select
              size="small"
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value as PaymentStatus | '')}
              displayEmpty
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="pending">Pendentes</MenuItem>
              <MenuItem value="paid">Pagos</MenuItem>
              <MenuItem value="cancelled">Cancelados</MenuItem>
            </Select>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Afiliado</TableCell>
                  <TableCell>Comissão</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Info</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        Nenhum pagamento encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.client?.name || '-'}</TableCell>
                      <TableCell>{payment.affiliate?.name || '-'}</TableCell>
                      <TableCell>R$ {payment.commission_value.toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={PAYMENT_STATUS_LABELS[payment.status]}
                          color={PAYMENT_STATUS_COLORS[payment.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          Criado: {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                        </Typography>
                        {payment.status === 'paid' && payment.paid_at && (
                          <Typography variant="body2" color="success.main">
                            Pago em {new Date(payment.paid_at).toLocaleDateString('pt-BR')}
                            {payment.paid_by && ` por ${payment.paid_by.name}`}
                          </Typography>
                        )}
                        {payment.status === 'cancelled' && payment.cancelled_at && (
                          <Typography variant="body2" color="error.main">
                            Cancelado em {new Date(payment.cancelled_at).toLocaleDateString('pt-BR')}
                            {payment.cancelled_by && ` por ${payment.cancelled_by.name}`}
                          </Typography>
                        )}
                        {payment.cancellation_reason && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Motivo: {payment.cancellation_reason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {payment.status === 'pending' && (
                            <Tooltip title="Marcar como pago">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleMarkPaid(payment.id)}
                              >
                                <PaidIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(payment.status === 'pending' || payment.status === 'paid') && (
                            <Tooltip title="Cancelar">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleOpenCancelPayment(payment)}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(payment.status === 'paid' || payment.status === 'cancelled') && (
                            <Tooltip title="Voltar para pendente">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleRevertToPending(payment.id)}
                              >
                                <UndoIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {payment.status === 'cancelled' && (
                            <Tooltip title="Excluir permanentemente">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeletePayment(payment)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={editUserOpen} onClose={() => setEditUserOpen(false)}>
        <DialogTitle>Editar Roles - {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <FormGroup sx={{ mt: 1 }}>
            {(['user', 'commercial', 'admin'] as UserRole[]).map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    checked={newRoles.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                  />
                }
                label={ROLE_LABELS[role]}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSaveRoles}
            variant="contained"
            disabled={saving || newRoles.length === 0}
          >
            {saving ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editCommissionOpen} onClose={() => setEditCommissionOpen(false)}>
        <DialogTitle>Editar Comissão - {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Valor da comissão (R$)"
            type="number"
            value={newCommission}
            onChange={(e) => setNewCommission(Number(e.target.value))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCommissionOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveCommission} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clientDetailsOpen}
        onClose={() => setClientDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            Histórico do Cliente - {selectedClient?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box>
              <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.background.default, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography>{selectedClient.email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Telefone
                    </Typography>
                    <Typography>{selectedClient.phone}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status Atual
                    </Typography>
                    <Box>
                      <Chip
                        label={CLIENT_STATUS_LABELS[selectedClient.status]}
                        color={CLIENT_STATUS_COLORS[selectedClient.status]}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Indicado por
                    </Typography>
                    <Typography>{selectedClient.affiliate?.name || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Comercial Responsável
                    </Typography>
                    <Typography>{selectedClient.commercial?.name || 'Não atribuído'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Data de Cadastro
                    </Typography>
                    <Typography>
                      {new Date(selectedClient.created_at).toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon fontSize="small" />
                Histórico de Atividades
              </Typography>

              {loadingLogs ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : clientLogs.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  Nenhum registro de atividade encontrado
                </Typography>
              ) : (
                <List sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                  {clientLogs.map((log, index) => (
                    <React.Fragment key={log.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography fontWeight="medium">
                                {log.description}
                              </Typography>
                              {log.action === 'STATUS_CHANGED' && (
                                <Chip
                                  label={`${log.previous_value} → ${log.new_value}`}
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(log.created_at).toLocaleString('pt-BR')}
                                {log.performed_by && ` • Realizado por: ${log.performed_by.name}`}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < clientLogs.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientDetailsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={passwordResetOpen}
        onClose={() => setPasswordResetOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PasswordResetIcon color="primary" />
            Recuperação de Senha - {selectedUser?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Gere o link abaixo e envie para {selectedUser?.email}. O link expira em 1 hora.
          </Typography>

          {passwordResetLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TextField
              fullWidth
              label="Link de recuperação de senha"
              value={passwordResetLink}
              InputProps={{ readOnly: true }}
              multiline
              minRows={2}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordResetOpen(false)}>Fechar</Button>
          <Button
            onClick={handleCopyPasswordResetLink}
            variant="outlined"
            startIcon={<CopyIcon />}
            disabled={passwordResetLoading || !passwordResetLink}
          >
            Copiar Link
          </Button>
          <Button
            onClick={handleSendPasswordResetEmail}
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            disabled={passwordResetLoading || passwordResetSending}
          >
            {passwordResetSending ? <CircularProgress size={20} /> : 'Enviar por Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para cancelar pagamento */}
      <Dialog open={cancelPaymentOpen} onClose={() => setCancelPaymentOpen(false)}>
        <DialogTitle>Cancelar Pagamento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Você está prestes a cancelar o pagamento de R$ {selectedPayment?.commission_value.toFixed(2)} 
            para {selectedPayment?.affiliate?.name}.
          </Typography>
          <TextField
            fullWidth
            label="Motivo do cancelamento (opcional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelPaymentOpen(false)}>Voltar</Button>
          <Button
            onClick={handleCancelPayment}
            variant="contained"
            color="warning"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Cancelar Pagamento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para excluir pagamento */}
      <Dialog open={deletePaymentOpen} onClose={() => setDeletePaymentOpen(false)}>
        <DialogTitle>Excluir Pagamento</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta ação é irreversível! O pagamento será removido permanentemente.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pagamento de R$ {selectedPayment?.commission_value.toFixed(2)} 
            para {selectedPayment?.affiliate?.name} (Cliente: {selectedPayment?.client?.name})
          </Typography>
          <TextField
            fullWidth
            label="Motivo da exclusão *"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            multiline
            rows={2}
            required
            error={!deleteReason.trim()}
            helperText="Informe o motivo da exclusão (obrigatório)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePaymentOpen(false)}>Voltar</Button>
          <Button
            onClick={handleDeletePayment}
            variant="contained"
            color="error"
            disabled={actionLoading || !deleteReason.trim()}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Excluir Pagamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
